import { Stars02 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion/accordion';
import { Button } from '@/lib/base/buttons/button';
import type {
  ClarifyingAnswer,
  ClarifyingQuestion as ClarifyingQuestionData,
} from '@/lib/proposals/core/types';
import {
  countAnsweredQuestions,
  hasGatingQuestion,
} from '@/lib/proposals/utils/clarifyingGating';

import { ClarifyingQuestion } from './ClarifyingQuestion';

// ── Module-scope constants ─────────────────────────────────────────────────────

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const SAVE_STATUS_LABEL: Partial<Record<SaveState, string>> = {
  saving: 'Saving…',
  saved: 'Saved',
  error: "We couldn't save",
};

// Reason shown on the Submit button's tooltip when it's disabled for a cause
// other than hasSubmitted/isSubmitting (each of which has its own message
// below) — the panel itself is locked, e.g. the proposal isn't pending or a
// chat turn is running.
const SUBMIT_LOCKED_TOOLTIP = "Answers can't be submitted right now";

// ── Module-scope sub-component ─────────────────────────────────────────────────
// Curries questionId into each callback so ClarifyingQuestion receives pre-bound
// handlers (avoids inline arrow functions per stable-reference rules).

interface ClarifyingQuestionWithHandlersProps {
  question: ClarifyingQuestionData;
  answer: ClarifyingAnswer | undefined;
  disabled: boolean;
  onSelectOption: (questionId: string, optionId: string) => void;
  onToggleOption: (questionId: string, optionId: string) => void;
  onSetOther: (questionId: string, text: string) => void;
  orgId?: number;
}

function ClarifyingQuestionWithHandlers({
  question,
  answer,
  disabled,
  onSelectOption,
  onToggleOption,
  onSetOther,
  orgId,
}: ClarifyingQuestionWithHandlersProps) {
  const handleSelectOption = useCallback(
    (optionId: string) => {
      onSelectOption(question.id, optionId);
    },
    [onSelectOption, question.id]
  );

  const handleToggleOption = useCallback(
    (optionId: string) => {
      onToggleOption(question.id, optionId);
    },
    [onToggleOption, question.id]
  );

  const handleSetOther = useCallback(
    (text: string) => {
      onSetOther(question.id, text);
    },
    [onSetOther, question.id]
  );

  return (
    <ClarifyingQuestion
      question={question}
      answer={answer}
      onSelectOption={handleSelectOption}
      onToggleOption={handleToggleOption}
      onSetOther={handleSetOther}
      disabled={disabled}
      orgId={orgId}
    />
  );
}
ClarifyingQuestionWithHandlers.displayName = 'ClarifyingQuestionWithHandlers';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  questions: ClarifyingQuestionData[];
  answers: Record<string, ClarifyingAnswer>;
  saveState: SaveState;
  isSubmitting: boolean;
  disabled: boolean;
  onSelectOption: (questionId: string, optionId: string) => void;
  onToggleOption: (questionId: string, optionId: string) => void;
  onSetOther: (questionId: string, text: string) => void;
  /** Resolves true when the answers were successfully submitted — the panel
   * collapses back to its summary bar on true (like a sent message) and stays
   * open on false so a failed save keeps the user's context. */
  onSubmit: () => Promise<boolean>;
  /** True when the current answers were already submitted and unchanged since
   * (durable via the envelope's answers_submitted_at). Disables resubmission
   * of an identical answer set; editing any answer re-arms Submit. */
  hasSubmitted: boolean;
  orgId?: number;
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * Collapsible clarifying-questions card.
 *
 * Renders in the proposal CONTENT region, directly under the Action
 * Overview — the agent's request for input reads before the action cards.
 * (Product decision 2026-09-01, task 4: the footer holds only the chat
 * input and the approve/reject buttons; nothing else renders there.)
 *
 * Expanded by default while unanswered — content space isn't scarce the way
 * footer space was. Collapses to its one-line summary bar after a
 * successful submit (like a sent message), and mounts collapsed when the
 * answers were already submitted (durable via answers_submitted_at).
 *
 * Answering is optional for ordinary questions — they never gate approval and
 * the panel stays freely collapsible. A GATING question (`gatesActions`) is
 * different: it keeps the action cards and Approve locked until its answers are
 * submitted and the refine turn finishes, so while any gating question is
 * unsubmitted the panel force-expands and cannot be collapsed away (gating
 * questions also render first — see the ordering below). The gate is the
 * submission, not the mere presence of an answer.
 */
export function ClarifyingQuestionsPanel({
  questions,
  answers,
  saveState,
  isSubmitting,
  disabled,
  onSelectOption,
  onToggleOption,
  onSetOther,
  onSubmit,
  hasSubmitted,
  orgId,
}: Props) {
  // Expanded by default while unsubmitted; controlled so a successful submit
  // can dismiss the card back to its summary bar (like a sent message).
  // Initialized once — the host keys this component by question_set_id, so a
  // new question set remounts it and re-evaluates.
  const [isExpanded, setIsExpanded] = useState(!hasSubmitted);
  const lockExpanded = hasGatingQuestion(questions) && !hasSubmitted;

  // Gating questions must answer first, so surface them ahead of the rest while
  // preserving each group's relative order (stable partition). Without this a
  // non-gating question that happens to precede a gating one would render first
  // in the force-expanded panel, hiding the question the user must answer to
  // unlock the actions.
  const orderedQuestions = useMemo(() => {
    if (!hasGatingQuestion(questions)) {
      return questions;
    }
    return [
      ...questions.filter(q => q.gatesActions === true),
      ...questions.filter(q => q.gatesActions !== true),
    ];
  }, [questions]);

  const handleExpandedChange = useCallback(
    (next: boolean) => {
      if (lockExpanded && !next) {
        return;
      }
      setIsExpanded(next);
    },
    [lockExpanded]
  );

  const handleSubmit = useCallback(async () => {
    const submitted = await onSubmit();
    if (submitted) {
      setIsExpanded(false);
    }
  }, [onSubmit]);

  const saveStatusLabel = SAVE_STATUS_LABEL[saveState] ?? null;
  const answeredCount = countAnsweredQuestions(questions, answers);
  const totalCount = questions.length;
  const progressStatus = `${answeredCount} of ${totalCount} questions answered.`;

  let progressLabel: string;
  if (hasSubmitted) {
    progressLabel = 'Answers sent';
  } else if (answeredCount === 0) {
    // "Click to answer" is an invitation to expand — it only makes sense on
    // the collapsed bar. While open, show plain progress instead.
    progressLabel = isExpanded
      ? `0 of ${totalCount} answered`
      : 'Click to answer';
  } else if (answeredCount < totalCount) {
    progressLabel = `${answeredCount} of ${totalCount} answered`;
  } else {
    progressLabel = 'All answered';
  }

  let submitDisabledTooltip: string | undefined;
  if (hasSubmitted) {
    submitDisabledTooltip = 'Answers already sent';
  } else if (isSubmitting) {
    submitDisabledTooltip = 'Submitting your answers…';
  } else if (disabled) {
    submitDisabledTooltip = SUBMIT_LOCKED_TOOLTIP;
  }

  return (
    <div
      data-component='clarifying-questions-panel'
      className='rounded-xl border border-[var(--color-border-primary)] bg-[var(--color-bg-primary)] shadow-xs'
    >
      {/* Announced to screen readers on change; no layout impact */}
      <div aria-atomic='true' aria-live='polite' className='sr-only'>
        {progressStatus}
      </div>

      <AccordionItem
        isExpanded={isExpanded || lockExpanded}
        onExpandedChange={handleExpandedChange}
      >
        <AccordionTrigger className='flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--color-bg-secondary)]'>
          <Stars02
            aria-hidden='true'
            className='size-4 shrink-0 text-fg-brand-primary'
          />
          <span className='flex-1 truncate text-body-small font-medium text-[var(--color-text-primary)]'>
            {totalCount === 1
              ? '1 clarifying question'
              : `${totalCount} clarifying questions`}
          </span>
          <span className='shrink-0 text-body-small text-[var(--color-text-secondary)]'>
            {progressLabel}
          </span>
          <AccordionChevron />
        </AccordionTrigger>

        <AccordionPanel className='flex flex-col gap-4 px-3 pb-3'>
          {/* Unconstrained list — the card scrolls with the content region,
              so no internal scroll area is needed (the 40vh cap existed only
              to keep the footer's chat input visible). */}
          <div className='flex flex-col gap-5 pt-1'>
            {orderedQuestions.map(q => (
              <ClarifyingQuestionWithHandlers
                key={q.id}
                question={q}
                answer={answers[q.id]}
                disabled={disabled}
                onSelectOption={onSelectOption}
                onToggleOption={onToggleOption}
                onSetOther={onSetOther}
                orgId={orgId}
              />
            ))}
          </div>

          <div className='flex items-center justify-between gap-3'>
            {/* Visible save-status indicator (mirrors the sr-only aria-live region) */}
            <p
              aria-hidden='true'
              className='text-body-small text-[var(--color-text-secondary)]'
            >
              {saveStatusLabel}
            </p>
            <Button
              color='primary'
              size='sm'
              data-component='clarifying-questions-panel'
              data-instance='submit-answers'
              isDisabled={disabled || isSubmitting || hasSubmitted}
              disabledTooltip={submitDisabledTooltip}
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              {hasSubmitted ? 'Answers sent' : 'Submit answers'}
            </Button>
          </div>
        </AccordionPanel>
      </AccordionItem>
    </div>
  );
}
